import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import {
  type PayoutRule,
  type TournamentRuleType,
  type VenuePayoutRule,
  ruleTypes,
  validPayoutRule,
} from './payoutRule.ts';

function RuleEditor({
  value,
  onChange,
  type,
}: {
  type: TournamentRuleType;
  value: PayoutRule;
  onChange: (rule: PayoutRule) => void;
}) {
  const { t } = useTranslation('tournament');
  const patchPool = (key: keyof PayoutRule['pool'], input: number) =>
    onChange({ ...value, pool: { ...value.pool, [key]: input } });
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        {type === 'TIMED' ? (
          <div className="text-sm sm:col-span-2">
            <p>{t('timedRankReserve', { rate: value.pool.rankBonusPercent })}</p>
            <p>{t('timedBaseFormula')}</p>
            {value.pool.rankBonusPercent !== 3 && (
              <div role="alert" className="mt-2 text-warning">
                <p>{t('timedLegacyRate')}</p>
                <button
                  type="button"
                  className="mt-2 rounded border px-3 py-2 min-h-11"
                  onClick={() =>
                    onChange({ ...value, pool: { primaryPercent: 97, rankBonusPercent: 3 } })
                  }
                >
                  {t('timedUseApprovedRate')}
                </button>
              </div>
            )}
          </div>
        ) : (
          (
            [
              ['primaryPercent', t('payoutUi.primary')],
              ['rankBonusPercent', t('payoutUi.rank')],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="text-sm">
              {label}
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                className="mt-1 w-full rounded border p-2 min-h-11 text-base"
                value={value.pool[key]}
                onChange={(event) => patchPool(key, Number(event.target.value))}
              />
            </label>
          ))
        )}
        <div className="text-sm">
          {t('payoutUi.rounding')}
          <p className="mt-1 rounded border p-2 min-h-11 text-base">{value.roundingUnit}</p>
        </div>
      </div>
      <p className="text-xs text-base-content/70">
        {type === 'TIMED' ? t('timedResidualExplanation') : t('payoutUi.ruleHint')}
      </p>
      {value.rankTiers.map((tier, index) => (
        <div key={index} className="border border-base-300 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-sm">
              {t('payoutUi.maxEntrants')}
              <input
                type="number"
                min="1"
                step="1"
                disabled={tier.maxEntrants === null}
                className="ml-2 w-24 rounded border p-1 min-h-11 text-base"
                value={tier.maxEntrants ?? ''}
                onChange={(event) =>
                  onChange({
                    ...value,
                    rankTiers: value.rankTiers.map((row, rowIndex) =>
                      rowIndex === index
                        ? { ...row, maxEntrants: Number(event.target.value) }
                        : row,
                    ),
                  })
                }
              />
            </label>
            {tier.maxEntrants === null && (
              <span className="text-xs">{t('payoutUi.unlimited')}</span>
            )}
            {tier.maxEntrants !== null && (
              <button
                type="button"
                className="text-error text-sm min-h-11"
                onClick={() =>
                  onChange({
                    ...value,
                    rankTiers: value.rankTiers.filter((_, rowIndex) => rowIndex !== index),
                  })
                }
              >
                {t('payoutUi.remove')}
              </button>
            )}
          </div>
          <label className="block text-sm">
            {t('payoutUi.shares')}
            <input
              className="mt-1 w-full rounded border p-2 min-h-11 text-base"
              value={tier.shares.join(', ')}
              onChange={(event) =>
                onChange({
                  ...value,
                  rankTiers: value.rankTiers.map((row, rowIndex) =>
                    rowIndex === index
                      ? {
                          ...row,
                          shares: event.target.value.split(',').map((item) => Number(item.trim())),
                        }
                      : row,
                  ),
                })
              }
            />
          </label>
        </div>
      ))}
      <button
        type="button"
        className="rounded border px-3 py-2 min-h-11 text-sm"
        onClick={() =>
          onChange({
            ...value,
            rankTiers: [
              ...value.rankTiers.slice(0, -1),
              {
                maxEntrants: (value.rankTiers.at(-2)?.maxEntrants ?? 0) + 10,
                shares: [50, 30, 20],
              },
              value.rankTiers.at(-1)!,
            ],
          })
        }
      >
        {t('payoutUi.addTier')}
      </button>
    </div>
  );
}

export { RuleEditor };

export default function PayoutRuleSettingsPage() {
  const { t } = useTranslation('tournament');
  const [dirty, setDirty] = useState(false);
  const [draftVersion, setDraftVersion] = useState(0);
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<TournamentRuleType>('TIMED');
  const [draft, setDraft] = useState<PayoutRule | null>(null);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['venue-payout-rules'],
    queryFn: () => api.get<{ rules: VenuePayoutRule[] }>('/tournament/payout-rules'),
  });
  const current = data?.rules.find((item) => item.type === selected);
  useEffect(() => {
    if (current && !dirty) {
      setDraft(structuredClone(current.rule));
      setDraftVersion(current.version);
    }
  }, [current, dirty]);
  const save = useMutation({
    mutationFn: () =>
      api.put(`/tournament/payout-rules/${selected}`, {
        expectedVersion: draftVersion,
        reason: reason.trim(),
        rule: draft,
      }),
    onSuccess: async () => {
      setMessage(t('payoutUi.saved'));
      setDirty(false);
      setReason('');
      await queryClient.invalidateQueries({ queryKey: ['venue-payout-rules'] });
    },
    onError: (failure: unknown) =>
      setMessage(
        t(
          (failure as { response?: { status?: number } }).response?.status === 409
            ? 'payoutUi.conflict'
            : 'payoutUi.failed',
        ),
      ),
  });
  return (
    <AdminPage>
      <AdminPageHeader
        title={t('payoutUi.title')}
        back={
          <Link to="/tournament" className="inline-flex min-h-11 items-center text-info text-sm">
            {t('payoutUi.back')}
          </Link>
        }
        description={t('payoutUi.scope')}
      />
      <div className="admin-content-form space-y-5">
        <label className="block text-sm">
          {t('payoutUi.type')}
          <select
            className="ml-3 rounded border p-2 min-h-11 text-base"
            value={selected}
            onChange={(event) => {
              if (!dirty || window.confirm(t('payoutUi.discard'))) {
                setDirty(false);
                setReason('');
                setMessage('');
                setSelected(event.target.value as TournamentRuleType);
              }
            }}
          >
            {ruleTypes.map((type) => (
              <option key={type} value={type}>
                {t(`type.${type}`)}
              </option>
            ))}
          </select>
        </label>
        {isLoading && <p>{t('payoutUi.loading')}</p>}
        {error && (
          <p role="alert" className="text-error">
            {t('payoutUi.failed')}
          </p>
        )}
        {current && draft && (
          <section className="admin-panel space-y-4">
            <RuleEditor
              type={selected}
              value={draft}
              onChange={(value) => {
                setDirty(true);
                setDraft(value);
              }}
            />
            <label className="block text-sm">
              {t('payoutUi.reason')}
              <input
                className="mt-1 w-full rounded border p-2 min-h-11 text-base"
                value={reason}
                onChange={(event) => {
                  setDirty(true);
                  setReason(event.target.value);
                }}
              />
            </label>
            {!validPayoutRule(draft, selected) && (
              <p role="alert" className="text-error text-sm min-h-11">
                {t('payoutUi.invalid')}
              </p>
            )}
            <button
              type="button"
              className="rounded bg-primary px-4 py-2 min-h-11 text-primary-content disabled:opacity-50"
              disabled={!validPayoutRule(draft, selected) || !reason.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              {t('payoutUi.save')}
            </button>
            {message && (
              <p role="status" className="text-sm">
                {message}
              </p>
            )}
          </section>
        )}
      </div>
    </AdminPage>
  );
}
